import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Container, Stack, Typography } from "@mui/material";
import { DynamicFeed } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectAuth } from "../auth/authSlice";
import { RoleTypes } from "../auth/types/roleTypes";
import { UserTypes } from "../auth/types/authType";
import Toast from "../../components/Layout/Toast";
import { fetchWallPosts, selectWall } from "./wallSlices";
import { fetchOfficeUsers } from "./wallSearch";
import PostComposer from "./components/PostComposer";
import WallFilters, { WallFilterValues } from "./components/WallFilters";
import WallPostCard from "./components/WallPostCard";
import { WallPost, WallUser } from "./types/wallTypes";

const PAGE_SIZE = 20;

export default function Wall() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAuth);
  const { posts, totalCount, error, success } = useAppSelector(selectWall);

  const [officeUsers, setOfficeUsers] = useState<WallUser[]>([]);
  const [filters, setFilters] = useState<WallFilterValues>({});
  const [editingPost, setEditingPost] = useState<WallPost | null>(null);
  const skipRef = useRef(0);

  const roleNames = useMemo(
    () => (user?.roles ?? []).map((r) => r.displayName),
    [user],
  );
  const isSuper = user?.userType === UserTypes.Super;
  const canPost = isSuper || roleNames.includes(RoleTypes.PostWall);
  const canModerate = isSuper || roleNames.includes(RoleTypes.ModerateWall);

  useEffect(() => {
    fetchOfficeUsers().then(setOfficeUsers);
  }, []);

  const load = (override?: WallFilterValues) => {
    skipRef.current = 0;
    dispatch(
      fetchWallPosts({
        skip: 0,
        take: PAGE_SIZE,
        ...(override ?? filters),
      }),
    );
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMore = () => {
    skipRef.current += PAGE_SIZE;
    dispatch(
      fetchWallPosts({
        skip: skipRef.current,
        take: PAGE_SIZE,
        ...filters,
        append: true,
      }),
    );
  };

  const handleFilterChange = (values: WallFilterValues) => {
    setFilters(values);
    load(values);
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, md: 4 } }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ mb: 3 }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            boxShadow: (theme) =>
              `0 6px 16px ${theme.palette.primary.main}55`,
          }}
        >
          <DynamicFeed />
        </Box>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
            {t("wall")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {totalCount} {t("posts") ?? ""}
          </Typography>
        </Box>
      </Stack>

      {canPost && (
        <PostComposer
          officeUsers={officeUsers}
          editingPost={editingPost}
          onDone={() => setEditingPost(null)}
        />
      )}

      <WallFilters officeUsers={officeUsers} onChange={handleFilterChange} />

      {posts.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            mt: 6,
            py: 6,
            px: 2,
            borderRadius: 4,
            border: "1px dashed",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <DynamicFeed
            sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
          />
          <Typography color="text.secondary">{t("noPostsYet")}</Typography>
        </Box>
      ) : (
        posts.map((post) => (
          <WallPostCard
            key={post._id}
            post={post}
            currentUserId={user?._id}
            canModerate={canModerate}
            onEdit={setEditingPost}
          />
        ))
      )}

      {posts.length < totalCount && (
        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Button
            onClick={loadMore}
            variant="outlined"
            sx={{ borderRadius: 99, px: 4, textTransform: "none" }}
          >
            {t("loadMore")}
          </Button>
        </Box>
      )}

      {error && <Toast severity="error">{error.message}</Toast>}
      {success && <Toast severity="success">{success.message}</Toast>}
    </Container>
  );
}
